import { useEffect, useMemo, useState } from 'react';
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Copy, Download, ExternalLink, Inbox, Link2, Loader2, LogIn, Mail, Trash2 } from 'lucide-react';
import JSZip from 'jszip';
import { toast } from 'sonner';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
    AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
    Sheet, SheetContent, SheetHeader, SheetTitle
} from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/integrations/supabase/client';
import { useImpersonate } from '@/hooks/users/useImpersonate';
import { useProfileForm, useSubscriptionForm } from '@/hooks/users/useUserForm';
import {
    genieMailboxProvisioningStatusQueryKey, useCreateSubscription, useDeleteGenieMailboxes, useDeleteSubscription,
    useGenieMailboxProvisioningStatus, useLastWelcomeEmail, useProvisionGenieMailboxes, useResetPassword, useSendWelcomeEmail,
    useUpdateSubscription, useUpdateUser
} from '@/hooks/users/useUserManagement';
import { ConversationService } from '@/services/conversation/ConversationService';
import { ImpersonationService } from '@/services/users/ImpersonationService';
import { UserService } from '@/services/users/UserService';
import { Profile, ProfileWithSubscription, Roles } from '@/types/auth/auth';
import { AllSubscriptionsManager } from './AllSubscriptionsManager';
import { GenieSeatsNotice } from './GenieSeatsNotice';
import { StripeCustomerLink } from './StripeCustomerLink';
import { UnbilledPhoneNumbersNotice } from './UnbilledPhoneNumbersNotice';
import { ProfileFields } from './ProfileFields';
import { UserTeamTab } from './UserTeamTab';

const SYNC_CONNECTIONS = [
  'airtable',
  'anthropic',
  'asana',
  'github-app',
  'google-calendar',
  'google-sheet',
  'hubspot',
  'simpro',
  'slack',
  'supabase',
  'xero',
] as const;

interface UserEditDialogProps {
  user: ProfileWithSubscription;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: () => void;
  onDelete?: (user: ProfileWithSubscription) => void;
  initialTab?: 'profile' | 'subscription' | 'team' | 'links';
  impersonationRedirect?: string | null;
}

export const UserEditDialog = ({ user, isOpen, onClose, onUpdate, onDelete, initialTab = 'profile', impersonationRedirect }: UserEditDialogProps) => {
  const queryClient = useQueryClient();
  const { impersonate } = useImpersonate();
  const currentImpersonatedUserId = ImpersonationService.getImpersonatedUserId();
  const [newPassword, setNewPassword] = useState("");
  const [isDeleteSubscriptionDialogOpen, setIsDeleteSubscriptionDialogOpen] = useState(false);
  const [subscriptionToDelete, setSubscriptionToDelete] = useState<number | null>(null);
  const { formData, handleFieldChange, handleAvatarChange, isProfileChanged } = useProfileForm(user);
  const {
    selectedSubscriptionId,
    subscriptionData,
    handleSelectSubscription,
    handleSubscriptionFieldChange,
    startThirtyDayTrial,
    giveFreeSubscription,
    createNewSubscription,
    isSubscriptionChanged,
    resetSubscriptionData,
    isCreatingNew,
  } = useSubscriptionForm(user);
  const updateUserMutation = useUpdateUser();
  const createSubscriptionMutation = useCreateSubscription();
  const updateSubscriptionMutation = useUpdateSubscription();
  const deleteSubscriptionMutation = useDeleteSubscription();
  const { resetPassword, isPending: isResettingPassword } = useResetPassword();
  const [syncConnection, setSyncConnection] = useState<string>('');
  const [isGeneratingSyncLink, setIsGeneratingSyncLink] = useState(false);
  const [isExportingTranscripts, setIsExportingTranscripts] = useState(false);
  const [transcriptDateFrom, setTranscriptDateFrom] = useState('');
  const [transcriptDateTo, setTranscriptDateTo] = useState('');
  const sendWelcomeEmailMutation = useSendWelcomeEmail();
  const { data: lastWelcomeEmail } = useLastWelcomeEmail(user.id, isOpen);
  const [isConfirmingWelcomeResend, setIsConfirmingWelcomeResend] = useState(false);
  const provisionGenieMailboxesMutation = useProvisionGenieMailboxes();
  const { data: mailboxStatus } = useGenieMailboxProvisioningStatus(user.id, isOpen);
  const isProvisioningMailboxes = mailboxStatus?.isProvisioning ?? false;
  const mailboxTotal = mailboxStatus?.total ?? 0;
  const mailboxProvisioned = mailboxStatus?.provisioned ?? 0;
  const allMailboxesProvisioned = mailboxTotal > 0 && mailboxProvisioned === mailboxTotal;
  const deleteGenieMailboxesMutation = useDeleteGenieMailboxes();
  const [isDeleteMailboxesDialogOpen, setIsDeleteMailboxesDialogOpen] = useState(false);
  const [mailboxDeletionProgress, setMailboxDeletionProgress] = useState(0);
  const isDeletingMailboxes = deleteGenieMailboxesMutation.isPending;

  useEffect(() => {
    if (!isOpen) return;

    const channel = supabase.channel('agent-creation').on(
      'broadcast',
      { event: 'mailbox_bulk_provisioned' },
      ({ payload }: { payload: { userId: string; results: { agentId: string; agentName: string; status: 'provisioned' | 'skipped' | 'failed'; error?: string }[] } }) => {
        if (payload.userId !== user.id) return;

        queryClient.invalidateQueries({ queryKey: genieMailboxProvisioningStatusQueryKey(user.id) });

        const { results } = payload;
        const provisioned = results.filter((r) => r.status === 'provisioned').length;
        const skipped = results.filter((r) => r.status === 'skipped').length;
        const failed = results.filter((r) => r.status === 'failed');

        if (results.length === 0) {
          toast.info('This user has no genies to provision mailboxes for');
          return;
        }

        if (provisioned > 0) {
          toast.success(`${provisioned} mailbox${provisioned === 1 ? '' : 'es'} provisioned${skipped ? ` (${skipped} already had one)` : ''}`);
        } else if (skipped > 0 && failed.length === 0) {
          toast.info('All genies already have a mailbox');
        }

        if (failed.length > 0) {
          toast.error(`Failed to provision ${failed.length} mailbox${failed.length === 1 ? '' : 'es'}: ${failed.map((f) => f.agentName).join(', ')}`);
        }
      },
    ).subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [isOpen, user.id]);

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      let didSomething = false;

      if (newPassword.trim()) {
        if (newPassword.length < 6) {
          toast.error("Password must be at least 6 characters");
          return;
        }
        await resetPassword({ userId: user.id, password: newPassword });
        setNewPassword("");
        didSomething = true;
      }

      if (isProfileChanged()) {
        const profileUpdates: Partial<Profile> = {};
        if (formData.full_name !== user.full_name) profileUpdates.full_name = formData.full_name;
        if (formData.role !== user.role) profileUpdates.role = formData.role;
        if (formData.email !== user.email) profileUpdates.email = formData.email;
        if ((formData.avatar ?? null) !== (user.avatar_url ?? null)) profileUpdates.avatar_url = formData.avatar ?? undefined;

        await updateUserMutation.mutateAsync({
          userId: user.id,
          profileUpdates,
        });
        didSomething = true;
      }

      if (didSomething) {
        setNewPassword("");
        onUpdate();
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("An error occurred while updating the profile");
    }
  };

  const handleSaveSubscription = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (isSubscriptionChanged()) {
        const isCreatingNew = !selectedSubscriptionId;
        const isNewStatusActive = subscriptionData.status === "active" || subscriptionData.status === "trialing";

        if (isCreatingNew && isNewStatusActive) {
          const hasActiveOrTrialing = user.subscriptions?.some((sub) => sub.status === "active" || sub.status === "trialing");

          if (hasActiveOrTrialing) {
            toast.error(`User already has ${subscriptionData.status} subscription. Cancel it to create a new one.`);
            return;
          }
        }

        if (isCreatingNew) {
          await createSubscriptionMutation.mutateAsync({
            userId: user.id,
            status: subscriptionData.status,
            quantity: subscriptionData.quantity,
            extra_quantity: subscriptionData.extra_quantity,
            stripe_id: subscriptionData.stripe_id || null,
            trial_ends_at: subscriptionData.trial_ends_at || null,
            ends_at: subscriptionData.ends_at || null,
          });
        } else {
          await updateSubscriptionMutation.mutateAsync({
            userId: user.id,
            subscriptionId: selectedSubscriptionId!,
            status: subscriptionData.status,
            quantity: subscriptionData.quantity,
            extra_quantity: subscriptionData.extra_quantity,
            stripe_id: subscriptionData.stripe_id || null,
            trial_ends_at: subscriptionData.trial_ends_at || null,
            ends_at: subscriptionData.ends_at || null,
          });
        }

        resetSubscriptionData();
        onUpdate();
      }
    } catch (error) {
      console.error("Error updating subscription:", error);
      toast.error("An error occurred while updating the subscription");
    }
  };

  const handleStartTrial = () => {
    startThirtyDayTrial();
    toast.success("Trial settings applied - click Save Changes to confirm");
  };

  const handleGiveFreeSubscription = () => {
    giveFreeSubscription();
    toast.success("Free subscription settings applied - click Save Changes to confirm");
  };

  const handleGenerateSyncLink = async () => {
    if (!syncConnection) {
      toast.error("Select a connection first");
      return;
    }
    if (!user.email) {
      toast.error("User has no email");
      return;
    }

    setIsGeneratingSyncLink(true);
    try {
      const url = await UserService.generateSyncConsentLink(user.email, syncConnection);
      await navigator.clipboard.writeText(url);
      toast.success("Sync consent link copied to clipboard");
    } catch (error) {
      console.error("Error generating sync consent link:", error);
      toast.error("Failed to generate sync consent link");
    } finally {
      setIsGeneratingSyncLink(false);
    }
  };

  const handleExportTranscripts = async () => {
    setIsExportingTranscripts(true);
    try {
      const genies = await ConversationService.exportTranscripts(user.id, {
        dateFrom: transcriptDateFrom || undefined,
        dateTo: transcriptDateTo || undefined,
      });

      if (!genies.length) {
        toast.info("No conversation transcripts found for this user");
        return;
      }

      const zip = new JSZip();
      genies.forEach((genie) => {
        const safeName = genie.name.replace(/[\\/:*?"<>|]/g, "_").trim() || genie.agentId;
        zip.file(`${safeName}.txt`, genie.content);
      });

      const blob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${(user.full_name || user.email || user.id).replace(/[\\/:*?"<>|]/g, "_")}-transcripts.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting transcripts:", error);
      toast.error("Failed to export transcripts");
    } finally {
      setIsExportingTranscripts(false);
    }
  };

  const handleOpenStripeSubscription = (stripeId: string) => {
    window.open(`https://dashboard.stripe.com/subscriptions/${stripeId}`, "_blank");
  };

  const handleDeleteSubscription = (subscriptionId: number) => {
    setSubscriptionToDelete(subscriptionId);
    setIsDeleteSubscriptionDialogOpen(true);
  };

  const confirmDeleteSubscription = async () => {
    if (!subscriptionToDelete) return;

    try {
      await deleteSubscriptionMutation.mutateAsync({
        userId: user.id,
        subscriptionId: subscriptionToDelete,
      });
      setIsDeleteSubscriptionDialogOpen(false);
      setSubscriptionToDelete(null);
      resetSubscriptionData();
      onUpdate();
    } catch (error) {
      console.error("Error deleting subscription:", error);
    }
  };

  const allowedGenies = useMemo(() => {
    const isLive = (status: string | null) => status === "active" || status === "trialing";
    const seats = (user.subscriptions ?? []).reduce((total, sub) => {
      const row = sub.id === selectedSubscriptionId ? subscriptionData : sub;
      return isLive(row.status) ? total + (row.quantity || 0) + (row.extra_quantity || 0) : total;
    }, 0);
    const draftSeats = isCreatingNew && isLive(subscriptionData.status)
      ? (subscriptionData.quantity || 0) + (subscriptionData.extra_quantity || 0)
      : 0;
    return 1 + seats + draftSeats;
  }, [user.subscriptions, selectedSubscriptionId, subscriptionData, isCreatingNew]);

  const isPending =
    updateUserMutation.isPending ||
    isResettingPassword ||
    createSubscriptionMutation.isPending ||
    updateSubscriptionMutation.isPending ||
    deleteSubscriptionMutation.isPending;

  return (
    <>
      <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <SheetContent side="right" className="w-full sm:max-w-[900px] flex flex-col h-full overflow-hidden bg-genie-background">
          <SheetHeader className="sr-only">
            <SheetTitle>Edit User</SheetTitle>
          </SheetHeader>

          <Tabs key={user.id} defaultValue={initialTab} className="flex flex-col h-full min-h-0 flex-1 pt-4">
            <TabsList className="flex-shrink-0 grid w-full grid-cols-4">
              <TabsTrigger value="profile">Profile</TabsTrigger>
              <TabsTrigger value="subscription">Subscription</TabsTrigger>
              <TabsTrigger value="team">Team</TabsTrigger>
              <TabsTrigger value="links">Links and Actions</TabsTrigger>
            </TabsList>

            <div className="flex-1 overflow-y-auto min-h-0">
              {/* Profile Tab */}
              <TabsContent value="profile" className="mt-0 py-4 pr-4">
                <form onSubmit={handleSaveProfile} className="flex flex-col gap-5">
                  <div className="bg-white rounded-[10px] p-5 shadow-[0_3px_10px_rgba(0,0,0,0.05)]">
                    <ProfileFields
                      userId={user.id}
                      fullName={formData.full_name}
                      email={formData.email}
                      role={formData.role}
                      avatar={formData.avatar}
                      createdAt={user.created_at}
                      updatedAt={user.updated_at}
                      isInternalAdmin={true}
                      onFieldChange={handleFieldChange}
                      onAvatarChange={handleAvatarChange}
                      newPassword={newPassword}
                      onNewPasswordChange={setNewPassword}
                    />
                  </div>

                  <div className="flex gap-2.5">
                    <Button variant="outline" type="button" onClick={onClose} disabled={isPending}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={isPending} className="bg-genie-primary hover:bg-genie-primary/90">
                      {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Save Profile
                    </Button>
                  </div>
                </form>
              </TabsContent>

              {/* Links Tab */}
              <TabsContent value="links" className="mt-0 py-4 pr-4 flex flex-col gap-5">
                <div className="bg-white rounded-[10px] p-5 shadow-[0_3px_10px_rgba(0,0,0,0.05)]">
                  <div className="flex items-center mb-[18px]">
                    <div className="h-8 w-8 rounded-full bg-genie-primary/10 flex items-center justify-center mr-3">
                      <Mail className="h-5 w-5 text-genie-primary" />
                    </div>
                    <h2 className="text-xl font-semibold m-0">Welcome Email</h2>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {isConfirmingWelcomeResend ? (
                      <>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() =>
                            sendWelcomeEmailMutation.mutate(user.id, {
                              onSettled: () => setIsConfirmingWelcomeResend(false),
                            })
                          }
                          disabled={sendWelcomeEmailMutation.isPending}
                        >
                          {sendWelcomeEmailMutation.isPending ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Mail className="h-4 w-4 mr-2" />
                          )}
                          Yes, Send Again
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => setIsConfirmingWelcomeResend(false)}
                          disabled={sendWelcomeEmailMutation.isPending}
                        >
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() =>
                          lastWelcomeEmail
                            ? setIsConfirmingWelcomeResend(true)
                            : sendWelcomeEmailMutation.mutate(user.id)
                        }
                        disabled={sendWelcomeEmailMutation.isPending || !user.email}
                      >
                        {sendWelcomeEmailMutation.isPending ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Mail className="h-4 w-4 mr-2" />
                        )}
                        {lastWelcomeEmail ? "Resend Welcome Email" : "Send Welcome Email"}
                      </Button>
                    )}
                    <Button type="button" variant="ghost" asChild>
                      <Link to="/admin/system-emails" target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Preview / Edit Template
                      </Link>
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1.5">Sends the account welcome email with a set-password link to <CopyableEmail email={user.email} fallback="this user" />.</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {lastWelcomeEmail?.created_at
                      ? `${isConfirmingWelcomeResend ? "Already sent" : "Last sent"} ${new Date(lastWelcomeEmail.created_at).toLocaleString()}${lastWelcomeEmail.status === "failed" ? " (failed)" : ""}${isConfirmingWelcomeResend ? " — send another? The earlier set-password link stops working." : ""}`
                      : "Not sent yet."}
                  </p>
                </div>

                <div className="bg-white rounded-[10px] p-5 shadow-[0_3px_10px_rgba(0,0,0,0.05)]">
                  <div className="flex items-center mb-[18px]">
                    <div className="h-8 w-8 rounded-full bg-genie-primary/10 flex items-center justify-center mr-3">
                      <Download className="h-5 w-5 text-genie-primary" />
                    </div>
                    <h2 className="text-xl font-semibold m-0">Conversation Transcripts</h2>
                  </div>
                  <div className="flex flex-wrap items-end justify-between gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleExportTranscripts}
                      disabled={isExportingTranscripts}
                    >
                      {isExportingTranscripts ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4 mr-2" />
                      )}
                      Export All Transcripts
                    </Button>
                    <div className="flex flex-wrap items-end gap-2">
                      <div>
                        <Label htmlFor="transcript-date-from" className="block mb-1.5 font-medium text-sm">
                          From
                        </Label>
                        <Input
                          id="transcript-date-from"
                          type="date"
                          value={transcriptDateFrom}
                          onChange={(e) => setTranscriptDateFrom(e.target.value)}
                          max={transcriptDateTo || undefined}
                          className="w-auto"
                        />
                      </div>
                      <div>
                        <Label htmlFor="transcript-date-to" className="block mb-1.5 font-medium text-sm">
                          To
                        </Label>
                        <Input
                          id="transcript-date-to"
                          type="date"
                          value={transcriptDateTo}
                          onChange={(e) => setTranscriptDateTo(e.target.value)}
                          min={transcriptDateFrom || undefined}
                          className="w-auto"
                        />
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1.5">
                    Downloads a .zip with one transcript file per genie for this account. Leave dates blank to export everything.
                  </p>
                </div>

                <div className="bg-white rounded-[10px] p-5 shadow-[0_3px_10px_rgba(0,0,0,0.05)]">
                  <div className="flex items-center mb-[18px]">
                    <div className="h-8 w-8 rounded-full bg-genie-primary/10 flex items-center justify-center mr-3">
                      <Link2 className="h-5 w-5 text-genie-primary" />
                    </div>
                    <h2 className="text-xl font-semibold m-0">HG Sync Consent Link</h2>
                  </div>
                  <Label htmlFor="sync-connection" className="block mb-1.5 font-medium text-sm">
                    Connection
                  </Label>
                  <div className="flex gap-2">
                    <Select value={syncConnection} onValueChange={setSyncConnection}>
                      <SelectTrigger id="sync-connection" className="w-full">
                        <SelectValue placeholder="Select connection" />
                      </SelectTrigger>
                      <SelectContent>
                        {SYNC_CONNECTIONS.map((connection) => (
                          <SelectItem key={connection} value={connection}>
                            {connection}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleGenerateSyncLink}
                      disabled={!syncConnection || isGeneratingSyncLink}
                      className="flex-shrink-0"
                    >
                      {isGeneratingSyncLink ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Copy className="h-4 w-4 mr-2" />
                      )}
                      Generate & Copy
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1.5">Generates and copies a consent link for the user to connect this integration.</p>
                </div>

                <div className="bg-white rounded-[10px] p-5 shadow-[0_3px_10px_rgba(0,0,0,0.05)]">
                  <div className="flex items-center justify-between mb-[18px]">
                    <div className="flex items-center">
                      <div className="h-8 w-8 rounded-full bg-genie-primary/10 flex items-center justify-center mr-3">
                        <Inbox className="h-5 w-5 text-genie-primary" />
                      </div>
                      <h2 className="text-xl font-semibold m-0">Genie Mailboxes</h2>
                    </div>
                    {mailboxTotal > 0 && (
                      <span className="text-sm font-medium text-gray-500">
                        {mailboxProvisioned}/{mailboxTotal} provisioned
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => provisionGenieMailboxesMutation.mutate(user.id)}
                      disabled={provisionGenieMailboxesMutation.isPending || isProvisioningMailboxes || allMailboxesProvisioned}
                    >
                      {provisionGenieMailboxesMutation.isPending || isProvisioningMailboxes ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Inbox className="h-4 w-4 mr-2" />
                      )}
                      {isProvisioningMailboxes ? 'Generating…' : allMailboxesProvisioned ? 'All Mailboxes Provisioned' : 'Generate Genie Mailboxes'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsDeleteMailboxesDialogOpen(true)}
                      disabled={mailboxTotal === 0 || isDeletingMailboxes || isProvisioningMailboxes}
                      className="text-red-600 border-red-200 hover:text-red-700 hover:bg-red-50"
                    >
                      {isDeletingMailboxes ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4 mr-2" />
                      )}
                      {isDeletingMailboxes
                        ? `Deleting… ${Math.min(mailboxDeletionProgress, mailboxTotal)} of ${mailboxTotal} genies`
                        : 'Delete All Mailboxes'}
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1.5">
                    Provisions a mailbox for every genie owned by this user. Genies that already have a mailbox are skipped.
                    Deleting removes every mailbox this user's genies have, together with its mail history.
                  </p>
                </div>
              </TabsContent>

              {/* Team Tab */}
              <TabsContent value="team" className="mt-0 py-4 pr-4">
                <UserTeamTab user={user} onUserUpdated={onUpdate} />
              </TabsContent>

              {/* Subscription Tab */}
              <TabsContent value="subscription" className="mt-0 py-4 pr-4 space-y-6">
                <UnbilledPhoneNumbersNotice numbers={user.unbilled_phone_numbers} />
                {user.role !== Roles.Admin && <GenieSeatsNotice counts={user.genie_counts} allowed={allowedGenies} />}
                <StripeCustomerLink user={user} />
                <form onSubmit={handleSaveSubscription} className="space-y-6">
                  <AllSubscriptionsManager
                    subscriptions={user.subscriptions}
                    selectedSubscriptionId={selectedSubscriptionId}
                    subscriptionData={subscriptionData}
                    isCreatingNew={isCreatingNew}
                    onSelectSubscription={handleSelectSubscription}
                    onFieldChange={handleSubscriptionFieldChange}
                    onStartTrial={handleStartTrial}
                    onGiveFreeSubscription={handleGiveFreeSubscription}
                    onCreateNew={createNewSubscription}
                    onOpenStripeSubscription={handleOpenStripeSubscription}
                    onDeleteSubscription={handleDeleteSubscription}
                  />

                  <div className="flex gap-2.5">
                    <Button variant="outline" type="button" onClick={onClose} disabled={isPending}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={isPending} className="bg-genie-primary hover:bg-genie-primary/90">
                      {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Save Changes
                    </Button>
                  </div>
                </form>
              </TabsContent>
            </div>
          </Tabs>

          {/* Footer - Shared buttons */}
          <div className="flex-shrink-0 border-t pt-3 mt-auto">
            <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center sm:justify-start">
              <Button
                variant="ghost"
                type="button"
                onClick={() => impersonate(user.id, user.full_name || user.email || user.id, impersonationRedirect || undefined)}
                className="text-genie-secondary hover:text-genie-secondary hover:bg-genie-secondary/10 sm:w-auto"
                title={currentImpersonatedUserId === user.id ? "Currently impersonating" : "Impersonate user"}
                disabled={currentImpersonatedUserId === user.id || isPending}
              >
                <LogIn className="h-4 w-4 mr-2" />
                Impersonate
              </Button>
              {onDelete && (
                <Button
                  variant="ghost"
                  type="button"
                  onClick={() => onDelete(user)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 sm:w-auto"
                  title="Delete user"
                  disabled={isPending}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1.5">
              <span className="font-medium text-gray-700">Impersonating in Suite apps:</span> open Mail, Sync or Outbound
              from the Genie apps switcher while impersonating. Typing the app's address directly won't sign you in as
              this user. A Suite app keeps one sign-in per browser, so it stays signed in as this user in every tab
              until you open it from the switcher as yourself. Impersonate in an incognito window to leave your own
              Suite sessions untouched.
            </p>
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={isDeleteSubscriptionDialogOpen} onOpenChange={setIsDeleteSubscriptionDialogOpen}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg">Delete Subscription</AlertDialogTitle>
            <AlertDialogDescription className="text-sm mt-2">
              Are you sure you want to delete this subscription? This action cannot be undone and will permanently remove the subscription
              record.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-0">
            <AlertDialogCancel className="mt-0">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteSubscription}
              disabled={deleteSubscriptionMutation.isPending}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleteSubscriptionMutation.isPending ? "Deleting..." : "Delete Subscription"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={isDeleteMailboxesDialogOpen} onOpenChange={setIsDeleteMailboxesDialogOpen}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg">Delete All Genie Mailboxes</AlertDialogTitle>
            <AlertDialogDescription className="text-sm mt-2">
              This deletes the mailbox of every genie owned by {user.full_name || user.email || 'this user'}
              {mailboxProvisioned > 0 ? ` (${mailboxProvisioned} provisioned)` : ''}. Each address stops working and its
              mail history is permanently removed. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-0">
            <AlertDialogCancel className="mt-0">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setMailboxDeletionProgress(0);
                deleteGenieMailboxesMutation.mutate({ userId: user.id, onProgress: setMailboxDeletionProgress });
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete All Mailboxes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
